import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
  Modal,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  message,
  Tag,
  Alert,
} from 'antd'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import moment from 'moment'
import {
  getAeciHourlyEvents,
  getAeciPreviewEvent,
  submitAeciHourlyEvents,
} from '../aeciApi'
import { DATE_FORMAT_API } from 'enum'
import { OUTAGE_TYPES } from './eventTypeRules'

const { Option } = Select

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)
const ampCodeKey = e =>
  `${e.eventType}_${e.causeCode}_${e.eventStartDate}_${
    e.eventStartHour
  }_${e.eventStartMinute || 0}`
const LOCKED_STATUSES = new Set(['S', 'L', 'R'])
const CELL_W = 58

const hfmt = h => `H${String(h).padStart(2, '0')}`
const efmt = (date, hour, minute = 0) =>
  moment(date, 'MM/DD/YYYY')
    .hour(hour - 1)
    .minute(minute)
    .format('MM/DD/YYYY HH:mm')

// Event spans and preview-range hour columns are date+hour pairs, not bare hour
// numbers — a range like "H24 Jul/02 -> H02 Jul/03" has startHour > endHour, so
// comparing/building on raw hour numbers alone breaks across the date boundary.
const slotKey = m => m.format('YYYYMMDDHH')
const hourLabel = (m, showDate) =>
  `${showDate ? m.format('M/D ') : ''}${hfmt(m.hour() + 1)}`
const eventTimeSpan = e => ({
  start: moment(e.eventStartDate, 'MM/DD/YYYY')
    .startOf('day')
    .add(e.eventStartHour - 1, 'hours')
    .add(e.eventStartMinute || 0, 'minutes'),
  end: moment(e.eventEndDate, 'MM/DD/YYYY')
    .startOf('day')
    .add(e.eventEndHour - 1, 'hours')
    .add(e.eventEndMinute || 0, 'minutes'),
})

const SeverityRenderer = ({ value }) => {
  if (value == null || value === '') return null
  const v = Number(value)
  return (
    <Tag color={v === 0 ? 'green' : 'red'}>
      {v === 0 ? 'OK' : `Error (${v})`}
    </Tag>
  )
}

const errorColumnDefs = [
  {
    headerName: 'Severity',
    field: 'severity',
    width: 110,
    cellRenderer: SeverityRenderer,
  },
  { headerName: 'Error Code', field: 'errorCode', width: 110 },
  { headerName: 'Error', field: 'error', width: 400, flex: 1 },
  { headerName: 'Event #', field: 'eventNum', width: 90 },
]

const defaultColDef = { resizable: true, sortable: true }

// ─── EventSpanTimeline ───────────────────────────────────────────────────────

const EventSpanTimeline = ({
  rowData,
  startDate,
  startHour,
  endDate,
  endHour,
  rawEvents,
  ampCodes,
  onAmpCodeChange,
}) => {
  const [selectedIdx, setSelectedIdx] = useState(null)

  const spans = useMemo(() => rowData.map(eventTimeSpan), [rowData])

  // Full list of hourly slots from (startDate,startHour) through (endDate,endHour)
  // inclusive, as moments — NOT bare hour numbers, since startHour can be numerically
  // greater than endHour once the range crosses a date (e.g. H24 -> H02 next day).
  const allSlots = useMemo(() => {
    const first = moment(startDate)
      .startOf('day')
      .add(startHour - 1, 'hours')
    const last = moment(endDate)
      .startOf('day')
      .add(endHour - 1, 'hours')
    const slots = []
    for (let m = first.clone(); m.isSameOrBefore(last); m.add(1, 'hours')) {
      slots.push(m.clone())
    }
    return slots
  }, [startDate, startHour, endDate, endHour])

  const spansMultipleDays =
    allSlots.length > 0 &&
    !allSlots[0].isSame(allSlots[allSlots.length - 1], 'day')

  const columns = useMemo(() => {
    const activeSlots = allSlots.filter(m =>
      spans.some(
        ({ start, end }) =>
          m.isBefore(end) &&
          m
            .clone()
            .add(1, 'hours')
            .isAfter(start)
      )
    )
    const cols = []
    activeSlots.forEach((m, i) => {
      if (i > 0) {
        const expectedNext = activeSlots[i - 1].clone().add(1, 'hours')
        if (!m.isSame(expectedNext)) {
          const from = expectedNext
          const to = m.clone().subtract(1, 'hours')
          cols.push({
            type: 'gap',
            key: `gap-${slotKey(from)}`,
            label: from.isSame(to)
              ? hourLabel(from, spansMultipleDays)
              : `${hourLabel(from, spansMultipleDays)}–${hourLabel(
                  to,
                  spansMultipleDays
                )}`,
          })
        }
      }
      cols.push({ type: 'hour', key: slotKey(m), m })
    })
    return cols
  }, [allSlots, spans, spansMultipleDays])

  if (!rowData.length) {
    return (
      <div style={{ padding: '20px 0', color: '#888', fontSize: 13 }}>
        No events found for the selected range.
      </div>
    )
  }

  const TH_BASE = {
    padding: '5px 4px',
    background: '#fafafa',
    borderBottom: '2px solid #d9d9d9',
    fontWeight: 700,
    fontSize: 11,
    whiteSpace: 'nowrap',
  }

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 12, color: '#595959' }}>
        <strong>{rowData.length}</strong> event record
        {rowData.length !== 1 ? 's' : ''} will be created
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            width: 'auto',
          }}
        >
          <colgroup>
            <col style={{ width: 170 }} />
            {columns.map(col =>
              col.type === 'gap' ? (
                <col key={col.key} style={{ width: 38 }} />
              ) : (
                <col key={col.key} style={{ width: CELL_W }} />
              )
            )}
            <col style={{ width: 80 }} />
            <col style={{ width: 96 }} />
            <col style={{ minWidth: 200 }} />
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  ...TH_BASE,
                  padding: '5px 10px',
                  borderRight: '1px solid #d9d9d9',
                  textAlign: 'left',
                  fontSize: 12,
                }}
              >
                Event Type
              </th>
              {columns.map(col =>
                col.type === 'gap' ? (
                  <th
                    key={col.key}
                    title={`No events: ${col.label}`}
                    style={{
                      ...TH_BASE,
                      color: '#bfbfbf',
                      letterSpacing: 1,
                      textAlign: 'center',
                    }}
                  >
                    ···
                  </th>
                ) : (
                  <th
                    key={col.key}
                    style={{
                      ...TH_BASE,
                      borderRight: '1px solid #f0f0f0',
                      textAlign: 'center',
                    }}
                  >
                    {hourLabel(col.m, spansMultipleDays)}
                  </th>
                )
              )}
              <th
                style={{
                  ...TH_BASE,
                  borderLeft: '1px solid #d9d9d9',
                  textAlign: 'center',
                }}
              >
                AvailCap
              </th>
              <th
                style={{
                  ...TH_BASE,
                  borderLeft: '1px solid #d9d9d9',
                  textAlign: 'center',
                }}
              >
                Amp Code
              </th>
              <th
                style={{
                  ...TH_BASE,
                  padding: '5px 10px',
                  borderLeft: '1px solid #d9d9d9',
                  textAlign: 'left',
                }}
              >
                Event Start → End
              </th>
            </tr>
          </thead>
          <tbody>
            {rowData.map((e, rowIdx) => {
              const isExpanded = selectedIdx === rowIdx
              const isMultiDay = e.eventStartDate !== e.eventEndDate
              // Built from rawEvents — the raw AECI_HOURLY_EVENT rows fetched per calendar
              // date in the preview range (see fetchRawEventsForRange) — not the single-day
              // grid's eventDataMap, so this works the same whether the run stays within one
              // date or crosses midnight into the next.
              const { start: runStart, end: runEnd } = spans[rowIdx]
              const calcItems = (() => {
                const subEvents = rawEvents
                  .filter(
                    ev =>
                      ev.utility === e.utility &&
                      ev.unit === e.unit &&
                      ev.eventType === e.eventType &&
                      String(ev.causeCode) === String(e.causeCode)
                  )
                  .map(ev => ({ ...ev, m: moment(ev.eventStart) }))
                  .filter(
                    ev => ev.m.isSameOrAfter(runStart) && ev.m.isBefore(runEnd)
                  )
                  .sort((a, b) => a.m.valueOf() - b.m.valueOf())

                const fmt = m => m.format(isMultiDay ? 'MM/DD HH:mm' : 'HH:mm')
                return subEvents.map((ev, i) => {
                  const end =
                    i < subEvents.length - 1 ? subEvents[i + 1].m : runEnd
                  const dur = end.diff(ev.m, 'minutes')
                  const cap = ev.availCap != null ? Number(ev.availCap) : null
                  return {
                    start: fmt(ev.m),
                    end: fmt(end),
                    cap,
                    dur,
                    capXDur: cap != null ? cap * dur : null,
                  }
                })
              })()
              const totalDur = calcItems.reduce((s, r) => s + r.dur, 0)
              const totalCapXDur = calcItems.reduce(
                (s, r) => s + (r.capXDur != null ? r.capXDur : 0),
                0
              )
              const avg = totalDur > 0 ? totalCapXDur / totalDur : 0
              return (
                <>
                  <tr
                    key={rowIdx}
                    style={{
                      cursor: 'pointer',
                      background: isExpanded ? '#f6ffed' : undefined,
                    }}
                    onClick={() => setSelectedIdx(isExpanded ? null : rowIdx)}
                  >
                    <td
                      style={{
                        padding: '4px 10px',
                        borderBottom: '1px solid #f0f0f0',
                        borderRight: '1px solid #d9d9d9',
                        whiteSpace: 'nowrap',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <span>
                        {e.eventType} ({e.causeCode})
                      </span>
                    </td>

                    {columns.map(col => {
                      if (col.type === 'gap') {
                        return (
                          <td
                            key={col.key}
                            style={{
                              padding: '3px 2px',
                              textAlign: 'center',
                              fontSize: 10,
                              color: '#d9d9d9',
                              background: '#fafafa',
                              borderBottom: '1px solid #f0f0f0',
                              letterSpacing: 1,
                            }}
                          >
                            ···
                          </td>
                        )
                      }
                      const { start, end } = spans[rowIdx]
                      const active =
                        col.m.isBefore(end) &&
                        col.m
                          .clone()
                          .add(1, 'hours')
                          .isAfter(start)
                      const isStart =
                        active && col.m.isSame(start.clone().startOf('hour'))
                      const isEnd =
                        active &&
                        col.m
                          .clone()
                          .add(1, 'hours')
                          .isSameOrAfter(end)
                      const bg = active ? '#f6ffed' : '#fafafa'
                      const fg = active ? '#389e0d' : '#bfbfbf'
                      const borderTop = active ? '2px solid #b7eb8f' : 'none'
                      const borderBottom = active
                        ? '2px solid #b7eb8f'
                        : '1px solid #f0f0f0'
                      const borderLeft = isStart
                        ? '2px solid #b7eb8f'
                        : active
                        ? 'none'
                        : '1px solid #f0f0f0'
                      const borderRight = isEnd
                        ? '2px solid #b7eb8f'
                        : active
                        ? 'none'
                        : '1px solid #f0f0f0'
                      return (
                        <td
                          key={col.key}
                          style={{
                            padding: '3px 4px',
                            textAlign: 'center',
                            fontSize: 11,
                            background: bg,
                            color: fg,
                            borderLeft,
                            borderRight,
                            borderTop,
                            borderBottom,
                            fontVariantNumeric: 'tabular-nums',
                            userSelect: 'none',
                          }}
                        >
                          {active
                            ? e.availCap != null
                              ? Number(e.availCap).toFixed(1)
                              : '—'
                            : ''}
                        </td>
                      )
                    })}

                    <td
                      style={{
                        padding: '4px 8px',
                        textAlign: 'center',
                        borderBottom: '1px solid #f0f0f0',
                        borderLeft: '1px solid #d9d9d9',
                        fontSize: 11,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {e.availCap != null ? Number(e.availCap).toFixed(1) : '—'}
                    </td>
                    <td
                      style={{
                        padding: '3px 6px',
                        textAlign: 'center',
                        borderBottom: '1px solid #f0f0f0',
                        borderLeft: '1px solid #d9d9d9',
                      }}
                      onClick={ev => ev.stopPropagation()}
                    >
                      {OUTAGE_TYPES.has(e.eventType) ? (
                        <Select
                          size="small"
                          style={{ width: 76 }}
                          value={ampCodes[ampCodeKey(e)] || ''}
                          onChange={val => onAmpCodeChange(ampCodeKey(e), val)}
                        >
                          <Option value="">—</Option>
                          <Option value="T1">T1</Option>
                          <Option value="T2">T2</Option>
                        </Select>
                      ) : null}
                    </td>
                    <td
                      style={{
                        padding: '4px 10px',
                        borderBottom: '1px solid #f0f0f0',
                        borderLeft: '1px solid #d9d9d9',
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        color: '#262626',
                      }}
                    >
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {efmt(
                          e.eventStartDate,
                          e.eventStartHour,
                          e.eventStartMinute || 0
                        )}
                      </span>
                      <span style={{ color: '#8c8c8c', margin: '0 3px' }}>
                        →
                      </span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {e.eventEndMinute > 0
                          ? efmt(
                              e.eventEndDate,
                              e.eventEndHour,
                              e.eventEndMinute
                            )
                          : efmt(e.eventEndDate, e.eventEndHour)}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && calcItems.length === 0 && (
                    <tr key={`calc-${rowIdx}`}>
                      <td
                        colSpan={999}
                        style={{
                          padding: '8px 16px',
                          background: '#f9f9f9',
                          borderBottom: '2px solid #b7eb8f',
                          color: '#8c8c8c',
                          fontSize: 12,
                        }}
                      >
                        {rawEvents.length === 0
                          ? 'Loading sub-event breakdown…'
                          : 'No sub-event breakdown found for this run.'}
                      </td>
                    </tr>
                  )}
                  {isExpanded && calcItems.length > 0 && (
                    <tr key={`calc-${rowIdx}`}>
                      <td
                        colSpan={999}
                        style={{
                          padding: '8px 16px',
                          background: '#f9f9f9',
                          borderBottom: '2px solid #b7eb8f',
                        }}
                      >
                        <table
                          style={{
                            borderCollapse: 'collapse',
                            fontSize: 11,
                            width: 'auto',
                          }}
                        >
                          <thead>
                            <tr style={{ background: '#fafafa' }}>
                              {[
                                'Start',
                                'End',
                                'Cap',
                                'Dur (min)',
                                'Cap × Dur',
                              ].map(h => (
                                <th
                                  key={h}
                                  style={{
                                    padding: '3px 10px',
                                    borderBottom: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {calcItems.map((r, i) => (
                              <tr key={i}>
                                <td
                                  style={{
                                    padding: '2px 10px',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {r.start}
                                </td>
                                <td
                                  style={{
                                    padding: '2px 10px',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {r.end}
                                </td>
                                <td
                                  style={{
                                    padding: '2px 10px',
                                    textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {r.cap != null ? r.cap.toFixed(1) : '—'}
                                </td>
                                <td
                                  style={{
                                    padding: '2px 10px',
                                    textAlign: 'right',
                                  }}
                                >
                                  {r.dur}
                                </td>
                                <td
                                  style={{
                                    padding: '2px 10px',
                                    textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {r.capXDur != null
                                    ? r.capXDur.toFixed(1)
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                            <tr
                              style={{
                                borderTop: '1px solid #d9d9d9',
                                fontWeight: 700,
                              }}
                            >
                              <td colSpan={3} style={{ padding: '3px 10px' }}>
                                Total
                              </td>
                              <td
                                style={{
                                  padding: '3px 10px',
                                  textAlign: 'right',
                                }}
                              >
                                {totalDur}
                              </td>
                              <td
                                style={{
                                  padding: '3px 10px',
                                  textAlign: 'right',
                                }}
                              >
                                {totalCapXDur.toFixed(1)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          <strong>Weighted AvailCap</strong> ={' '}
                          {totalCapXDur.toFixed(1)} ÷ {totalDur} ={' '}
                          <strong style={{ color: '#1890ff' }}>
                            {avg.toFixed(2)}
                          </strong>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── ViewSubmitModal ─────────────────────────────────────────────────────────

const ViewSubmitModal = ({
  visible,
  onCancel,
  onSubmitSuccess,
  unitId,
  unitName,
  selectedDate,
  apiTargetInfo,
  unitHourlyRows,
}) => {
  const [startDate, setStartDate] = useState(selectedDate || moment())
  const [startHour, setStartHour] = useState(1)
  const [endDate, setEndDate] = useState(selectedDate || moment())
  const [endHour, setEndHour] = useState(24)
  const [rowData, setRowData] = useState([])
  const [rawEvents, setRawEvents] = useState([])
  const [ampCodes, setAmpCodes] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitErrors, setSubmitErrors] = useState(null)

  const lockedHours = useMemo(() => {
    const statusRow = (unitHourlyRows || []).find(
      r => r.metric === 'Hourly Status'
    )
    if (!statusRow) return new Set()
    return new Set(HOURS.filter(h => LOCKED_STATUSES.has(statusRow[`h${h}`])))
  }, [unitHourlyRows])

  useEffect(() => {
    if (!visible) {
      setRowData([])
      setRawEvents([])
      setAmpCodes({})
      setSubmitErrors(null)
      return
    }
    if (selectedDate) {
      setStartDate(selectedDate)
      setEndDate(selectedDate)
      const available = HOURS.filter(h => !lockedHours.has(h))
      if (available.length > 0) {
        setStartHour(available[0])
        setEndHour(available[available.length - 1])
      }
    }
  }, [visible, selectedDate, lockedHours])

  useEffect(() => {
    setRowData([])
    setRawEvents([])
    setAmpCodes({})
    setSubmitErrors(null)
  }, [startDate, startHour, endDate, endHour])

  const currentParams = useCallback(
    () => ({
      unitId,
      startDate: startDate.format(DATE_FORMAT_API),
      startHour,
      endDate: endDate.format(DATE_FORMAT_API),
      endHour,
    }),
    [unitId, startDate, startHour, endDate, endHour]
  )

  // The per-sub-event breakdown needs the raw AECI_HOURLY_EVENT rows for every calendar
  // date the preview range touches — the main grid only ever holds a single day's events,
  // which can't represent a run that continues onto the next date.
  const fetchRawEventsForRange = useCallback(() => {
    const dates = []
    for (
      let d = moment(startDate).startOf('day');
      d.isSameOrBefore(endDate, 'day');
      d.add(1, 'day')
    ) {
      dates.push(d.format(DATE_FORMAT_API))
    }
    if (dates.length === 0) {
      setRawEvents([])
      return
    }
    let remaining = dates.length
    const merged = []
    dates.forEach(date => {
      getAeciHourlyEvents(apiTargetInfo)({ unitId, date }).fork(
        () => {
          remaining -= 1
          if (remaining === 0) setRawEvents(merged)
        },
        events => {
          merged.push(...events)
          remaining -= 1
          if (remaining === 0) setRawEvents(merged)
        }
      )
    })
  }, [apiTargetInfo, unitId, startDate, endDate])

  const handlePreview = useCallback(() => {
    if (!unitId) return
    setLoading(true)
    setSubmitErrors(null)
    getAeciPreviewEvent(apiTargetInfo)(currentParams()).fork(
      () => {
        message.error('Failed to load preview')
        setLoading(false)
      },
      data => {
        setRowData(data)
        setLoading(false)
        fetchRawEventsForRange()
      }
    )
  }, [apiTargetInfo, unitId, currentParams, fetchRawEventsForRange])

  const handleAmpCodeChange = useCallback((key, val) => {
    setAmpCodes(prev => ({ ...prev, [key]: val }))
  }, [])

  const handleSubmit = useCallback(() => {
    if (!unitId) return
    setSubmitLoading(true)
    submitAeciHourlyEvents(apiTargetInfo)({
      ...currentParams(),
      ampCodes,
    }).fork(
      () => {
        message.error('Submit failed')
        setSubmitLoading(false)
      },
      errors => {
        setSubmitLoading(false)
        setSubmitErrors(errors)
        const allOk = errors.every(
          e => e.severity == null || Number(e.severity) === 0
        )
        if (allOk) {
          message.success('Submitted successfully')
          onCancel()
          if (onSubmitSuccess) onSubmitSuccess()
        } else {
          message.warning('Submit completed with errors — see results below')
        }
      }
    )
  }, [
    apiTargetInfo,
    unitId,
    currentParams,
    ampCodes,
    onCancel,
    onSubmitSuccess,
  ])

  const validationErrors = useMemo(() => {
    const rows = unitHourlyRows || []
    const errors = []
    const hoursToCheck = HOURS.filter(h => h >= startHour && h <= endHour)

    // Only validate hours that have at least one event cell value in the hourly grid
    const eventRows = rows.filter(r => r.rowType === 'event')
    const hoursToValidate = hoursToCheck.filter(h =>
      eventRows.some(er => {
        const v = er[`h${h}`]
        return v != null && v !== '' && !isNaN(Number(v))
      })
    )

    const unallocRow = rows.find(r => r.metric === 'Unallocated')
    if (!unallocRow) {
      errors.push(
        'Unallocated data is not available — please retrieve data before submitting'
      )
    } else {
      const nullHours = hoursToValidate.filter(h => {
        const v = unallocRow[`h${h}`]
        return v == null || v === ''
      })
      if (nullHours.length > 0)
        errors.push(
          `Unallocated is not available for: ${nullHours
            .map(h => hfmt(h))
            .join(', ')}`
        )

      // Block submit if any event hour is over-allocated (unallocated < 0)
      const overAllocHours = hoursToValidate.filter(h => {
        const v = unallocRow[`h${h}`]
        if (v == null || v === '') return false
        return parseFloat(v) < -0.001
      })
      overAllocHours.forEach(h => {
        const v = parseFloat(unallocRow[`h${h}`])
        errors.push(
          `${hfmt(h)} is over-allocated (Unallocated: ${v.toFixed(1)})`
        )
      })
    }

    return errors
  }, [unitHourlyRows, startHour, endHour])

  // Only hide events the server flagged as already submitted
  const visibleRowData = useMemo(
    () => rowData.filter(e => e.isSubmitted !== 'S'),
    [rowData]
  )

  const hasEvents = visibleRowData.length > 0
  const hasSeverityError = visibleRowData.some(r => Number(r.severity) === 1)
  const canSubmit =
    hasEvents && !hasSeverityError && validationErrors.length === 0
  const title = unitId
    ? `View & Approve — ${unitName || unitId}`
    : 'View & Approve'

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitLoading}
          disabled={!canSubmit}
        >
          Submit
        </Button>,
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
      width={1100}
      destroyOnClose
    >
      {validationErrors.length > 0 && (
        <Alert
          type="error"
          style={{ marginBottom: 10 }}
          message="Cannot Submit"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {validationErrors.map(e => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      )}

      <Row
        gutter={8}
        style={{ marginBottom: 12, alignItems: 'center' }}
        type="flex"
      >
        <Col>
          <span>Start Date:</span>
        </Col>
        <Col>
          <DatePicker
            value={startDate}
            onChange={v => v && setStartDate(v)}
            allowClear={false}
            format="MMM/DD/YYYY"
          />
        </Col>
        <Col>
          <span>Start Hour:</span>
        </Col>
        <Col>
          <Select
            value={startHour}
            onChange={setStartHour}
            style={{ width: 80 }}
          >
            {HOURS.filter(h => !lockedHours.has(h)).map(h => (
              <Option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <span>End Date:</span>
        </Col>
        <Col>
          <DatePicker
            value={endDate}
            onChange={v => v && setEndDate(v)}
            allowClear={false}
            format="MMM/DD/YYYY"
          />
        </Col>
        <Col>
          <span>End Hour:</span>
        </Col>
        <Col>
          <Select value={endHour} onChange={setEndHour} style={{ width: 80 }}>
            {HOURS.filter(h => !lockedHours.has(h)).map(h => (
              <Option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={handlePreview}
            loading={loading}
            disabled={!unitId}
          >
            Preview
          </Button>
        </Col>
      </Row>

      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          padding: '10px 12px',
          minHeight: 80,
          background: '#fff',
          marginBottom: submitErrors != null ? 12 : 0,
        }}
      >
        {visibleRowData.length > 0 ? (
          <EventSpanTimeline
            rowData={visibleRowData}
            startDate={startDate}
            startHour={startHour}
            endDate={endDate}
            endHour={endHour}
            rawEvents={rawEvents}
            ampCodes={ampCodes}
            onAmpCodeChange={handleAmpCodeChange}
          />
        ) : (
          <div style={{ color: '#bfbfbf', fontSize: 13, paddingTop: 8 }}>
            Click <strong>Preview</strong> to see events that will be submitted.
          </div>
        )}
      </div>

      {submitErrors != null && (
        <>
          <div style={{ margin: '12px 0 6px', fontWeight: 'bold' }}>
            Submit Results
            {submitErrors.every(
              e => e.severity == null || Number(e.severity) === 0
            ) && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                All Passed
              </Tag>
            )}
          </div>
          <div
            className="ag-theme-alpine"
            style={{ height: 200, width: '100%' }}
          >
            <AgGridReact
              columnDefs={errorColumnDefs}
              rowData={submitErrors}
              defaultColDef={defaultColDef}
            />
          </div>
        </>
      )}
    </Modal>
  )
}

export default ViewSubmitModal
